<template>
  <section
    ref="bannerContainerRef"
    class="relative h-screen max-h-[700px] md:max-h-[800px] flex items-center justify-center -mt-[50px] overflow-hidden touch-pan-y select-none"
    aria-roledescription="carrossel"
    aria-label="Banners promocionais"
  >
    <template v-if="banners.length > 0 && banners[current]">
      <a
        v-if="banners[current].link"
        :href="banners[current].link"
        rel="noopener noreferrer"
        target="_self"
        :aria-label="`${banners[current].altText || 'Banner ' + (current + 1)} (abre na mesma janela)`"
         class="block"
         draggable="false"
      >
        <img
          :key="`img-${banners[current].id}`"
          :src="`/banner/${banners[current].imagem}`"
          :alt="banners[current].altText || `Banner ${current + 1}`"
          class="max-w-full max-h-full object-contain"
          draggable="false"
        />
      </a>
      <img
        v-else
        :key="`img-${banners[current].id}`"
        :src="`/banner/${banners[current].imagem}`"
        :alt="banners[current].altText || `Banner ${current + 1}`"
        class="max-w-full max-h-full object-contain"
        draggable="false"
      />
    </template>
    <div v-else class="text-gray-500 animate-pulse">
       Carregando banners...
    </div>

    <div
        v-if="banners.length > 1"
        class="absolute sm:bottom-12 md:bottom-32 lg:bottom-32 left-0 right-0 flex justify-center space-x-3 z-10"
        :style="windowWidth < 640 ? 'bottom: 160px' : ''"
        role="tablist"
        aria-label="Navegação dos banners"
      >

      <button
        v-for="(banner, index) in banners"
        :key="`dot-${banner.id}`"
        @click="goToBanner(index)"
        :class="[
          'w-3 h-3 rounded-full transition duration-300 ease-in-out',
          current === index ? 'bg-white scale-125 opacity-100' : 'bg-gray-500 opacity-70 hover:opacity-100 hover:bg-gray-400'
        ]"
        :aria-label="`Ir para o banner ${index + 1}`"
        :aria-selected="current === index ? 'true' : 'false'"
        role="tab"
      >
      </button>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, onBeforeUnmount } from 'vue'
import axios from '@/services/api'
import { useSwipe } from '@vueuse/core'

const banners = ref([])
const current = ref(0)
const bannerContainerRef = ref(null)
let autoPlayInterval = null
const autoPlayDelay = 5000
const swipeThreshold = 40;
const windowWidth = ref(window.innerWidth)

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', updateWindowWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowWidth)
})


const carregarBanners = async () => {
  try {
    const { data } = await axios.get('/api/banners/listar')
    const hoje = new Date()

    banners.value = data
      .filter(b => {
        if (!b.ativo) return false
        b.altText = b.alt || '';

        if (b.tipo === 'TEMPORARIO') {
          if (!b.dataInicio || !b.dataFim) return false
          const inicio = new Date(b.dataInicio)
          const fim = new Date(b.dataFim)
          inicio.setHours(0, 0, 0, 0);
          fim.setHours(23, 59, 59, 999);
          return hoje >= inicio && hoje <= fim
        }
        return true
      })
      .sort((a, b) => a.ordem - b.ordem)

      if (banners.value.length > 0 && current.value >= banners.value.length) {
          current.value = 0;
      } else if (banners.value.length === 0) {
          current.value = 0;
      }

  } catch (error) {
    console.error('Erro ao carregar banners:', error)
    banners.value = [];
    current.value = 0;
  } finally {
      resetAutoPlay();
  }
}

const nextBanner = () => {
  if (banners.value.length > 0) {
    current.value = (current.value + 1) % banners.value.length;
  }
};

const prevBanner = () => {
  if (banners.value.length > 0) {
    current.value = (current.value - 1 + banners.value.length) % banners.value.length;
  }
};

const goToBanner = (index) => {
  if (index >= 0 && index < banners.value.length) {
    current.value = index;
    resetAutoPlay();
  }
};

const startAutoPlay = () => {
  stopAutoPlay();
  if (banners.value.length > 1) {
    autoPlayInterval = setInterval(nextBanner, autoPlayDelay);
  }
};

const stopAutoPlay = () => {
  if (autoPlayInterval) {
      clearInterval(autoPlayInterval);
      autoPlayInterval = null;
  }
};

const resetAutoPlay = () => {
  stopAutoPlay();
  startAutoPlay();
};

const { direction, lengthX, lengthY } = useSwipe(
  bannerContainerRef,
  {
    passive: true,
    threshold: swipeThreshold,
    onSwipeStart: () => {
      stopAutoPlay();
    },
    onSwipeEnd: (event, dir) => {
      const absLengthX = Math.abs(lengthX.value);
      const absLengthY = Math.abs(lengthY.value);

      if (absLengthX > swipeThreshold && absLengthX > absLengthY) {
        if (dir === 'left') {
          nextBanner();
        } else if (dir === 'right') {
          prevBanner();
        }
      }
       setTimeout(resetAutoPlay, 300);
    },
  }
);

onMounted(() => {
  carregarBanners();
});

onUnmounted(() => {
  stopAutoPlay();
});

</script>

<style scoped>
.touch-pan-y {
  touch-action: pan-y;
}
.select-none {
  user-select: none;
  -webkit-user-drag: none;
}
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}
</style>